import { useCallback, useEffect, useRef, useState } from 'react';
import {
  containerDoMime,
  escolherMimeTypeDeAudio,
  DURACAO_MAXIMA_SEGUNDOS
} from './audioRecorderFormat';

export interface UseAudioRecorderReturn {
  isRecording: boolean;
  recordingTime: number;
  audioBlob: Blob | null;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  cancelRecording: () => void;
}

function mensagemDeErro(erro: unknown): string {
  const nome = erro instanceof DOMException ? erro.name : '';

  if (nome === 'NotAllowedError' || nome === 'SecurityError') {
    return 'Permissão de microfone negada. Libere o acesso no navegador para gravar áudio.';
  }
  if (nome === 'NotFoundError' || nome === 'DevicesNotFoundError') {
    return 'Nenhum microfone encontrado neste dispositivo.';
  }
  if (nome === 'NotReadableError' || nome === 'TrackStartError') {
    return 'O microfone está em uso por outro programa.';
  }
  return 'Não foi possível acessar o microfone.';
}

// Gravação de áudio do chat. É o irmão enxuto do useScreenRecorder: um único
// stream de microfone, sem canvas, sem webcam e sem pausa.
export const useAudioRecorder = (): UseAudioRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // O auto-stop é disparado de dentro do intervalo, onde `stopRecording` ainda
  // não existe (é definido abaixo). O ref quebra a dependência circular.
  const pararRef = useRef<() => void>(() => {});

  const pararTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Encerrar as tracks é o que apaga o indicador de microfone ativo do
  // navegador. Sem isso a aba fica marcada como "gravando" depois de enviar.
  const liberarMicrofone = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setAudioBlob(null);
    setRecordingTime(0);
    chunksRef.current = [];

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Este navegador não permite gravar áudio.');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });
    } catch (err) {
      console.error('Erro ao acessar o microfone:', err);
      setError(mensagemDeErro(err));
      return;
    }

    const mimeType = escolherMimeTypeDeAudio(
      tipo => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(tipo)
    );

    if (!mimeType) {
      // O microfone já está aberto neste ponto — devolvê-lo antes de desistir
      // evita deixar a aba marcada como gravando por uma falha nossa.
      stream.getTracks().forEach(track => track.stop());
      setError('Este navegador não suporta nenhum formato de áudio compatível.');
      return;
    }

    streamRef.current = stream;

    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = evento => {
      if (evento.data && evento.data.size > 0) {
        chunksRef.current.push(evento.data);
      }
    };

    recorder.onerror = evento => {
      console.error('Erro durante a gravação de áudio:', evento);
      setError('A gravação falhou. Tente novamente.');
    };

    recorder.start();
    setIsRecording(true);

    timerRef.current = setInterval(() => {
      setRecordingTime(anterior => {
        const agora = anterior + 1;
        if (agora >= DURACAO_MAXIMA_SEGUNDOS) {
          pararRef.current();
        }
        return agora;
      });
    }, 1000);
  }, []);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    const recorder = mediaRecorderRef.current;

    if (!recorder || recorder.state === 'inactive') {
      return null;
    }

    return new Promise<Blob | null>(resolve => {
      recorder.onstop = () => {
        // O mime do Blob sai sem os parâmetros de codec de propósito: é ele que
        // vira o content-type do upload, e o bucket só lista os containers.
        const blob = new Blob(chunksRef.current, { type: containerDoMime(recorder.mimeType) });
        mediaRecorderRef.current = null;
        setAudioBlob(blob);
        setIsRecording(false);
        pararTimer();
        liberarMicrofone();
        resolve(blob);
      };

      recorder.stop();
    });
  }, [pararTimer, liberarMicrofone]);

  const cancelRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state !== 'inactive') {
      // Sem handler: o áudio descartado não deve virar Blob nenhum.
      recorder.onstop = null;
      recorder.stop();
    }

    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setAudioBlob(null);
    setIsRecording(false);
    setRecordingTime(0);
    pararTimer();
    liberarMicrofone();
  }, [pararTimer, liberarMicrofone]);

  useEffect(() => {
    pararRef.current = () => {
      void stopRecording();
    };
  }, [stopRecording]);

  // Fechar a sala (ou o popup) no meio de uma gravação não pode deixar o
  // microfone aberto.
  useEffect(() => {
    return () => {
      pararTimer();
      streamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, [pararTimer]);

  return {
    isRecording,
    recordingTime,
    audioBlob,
    error,
    startRecording,
    stopRecording,
    cancelRecording
  };
};
